import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Building2,
  Upload,
  ExternalLink,
  Image,
  Palette,
  ArrowRight,
  CheckCircle2,
  CheckCircle,
  Shield,
  Smartphone,
  ChevronDown,
  Home,
  User,
  LogOut,
  Share2,
  AlertCircle,
} from "lucide-react";
import { MainLayout } from "@/layouts/MainLayout";
import { useAuth } from "@/hooks/useAuth";
import { whitelabelApi } from "@/api/whitelabel.api";
import { fetchWhitelabelBranding } from "@/utils/branding";
import { getExternalURL } from "@/config";

const PRESET_THEMES = [
  { name: "Royal Blue & Gold", primary: "#3A53A5", accent: "#FCD00F" },
  { name: "Electric Blue & Cyan", primary: "#0B63F6", accent: "#01B2FD" },
  { name: "Emerald & Amber", primary: "#059669", accent: "#F59E0B" },
  { name: "Indigo & Sunlight", primary: "#4F46E5", accent: "#FBBF24" },
  { name: "Crimson & Peach", primary: "#E11D48", accent: "#FB923C" },
  { name: "Deep Navy & Sky", primary: "#0F172A", accent: "#38BDF8" },
];

type PreviewScreenMode = "login" | "home";

export const WhiteLabelling: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();

  const agentId =
    searchParams.get("agentId") ||
    localStorage.getItem("agent_id") ||
    localStorage.getItem("agentId") ||
    "";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [previewScreen, setPreviewScreen] = useState<PreviewScreenMode>("login");

  const [companyName, setCompanyName] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [themeColor, setThemeColor] = useState("#3A53A5");
  const [accentColor, setAccentColor] = useState("#FCD00F");
  const [isActive, setIsActive] = useState(true);
  const [useCustomTheme, setUseCustomTheme] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [logoInputType, setLogoInputType] = useState<"upload" | "url">("upload");

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/agent-login");
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (!agentId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    whitelabelApi
      .getWhitelabelConfig(agentId)
      .then((cfg) => {
        if (cfg) {
          setCompanyName(cfg.company_name || cfg.name || "");
          setLogoUrl(cfg.logo_url || "");
          setIsActive(cfg.is_active ?? true);
          setUseCustomTheme(Boolean(cfg.use_custom_theme));
          if (cfg.theme_color) setThemeColor(cfg.theme_color);
          if (cfg.accent_color) setAccentColor(cfg.accent_color);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch white label config", err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [agentId]);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setErrorMessage("Please select a valid image file (PNG, JPG, SVG, WebP).");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setErrorMessage("Logo size should be under 2MB.");
      return;
    }

    setUploading(true);
    setErrorMessage("");

    try {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result as string;
        setLogoUrl(base64);
        setUploading(false);
      };
      reader.onerror = () => {
        setErrorMessage("Error reading file.");
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (err: any) {
      setErrorMessage(err?.message || "Failed to process image.");
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!companyName.trim()) {
      setErrorMessage("Company / Brand Name is required.");
      return;
    }

    if (!logoUrl.trim()) {
      setErrorMessage("Brand Logo is required for white labelling.");
      return;
    }

    setSaving(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      await whitelabelApi.saveWhitelabelConfig({
        agent_id: agentId,
        company_name: companyName.trim(),
        logo_url: logoUrl.trim(),
        use_custom_theme: useCustomTheme,
        theme_color: useCustomTheme ? themeColor : "",
        accent_color: useCustomTheme ? accentColor : "",
        is_active: isActive,
      });

      setSuccessMessage("White labelling configuration saved successfully!");
      if (agentId) {
        fetchWhitelabelBranding(agentId);
      }
    } catch (err: any) {
      setErrorMessage(err?.message || "Failed to save configuration. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const rawCustomerBase =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")
      ? "http://localhost:3001"
      : getExternalURL("CUSTOMER_PORTAL");
  const customerBase = (rawCustomerBase || "https://login-dev.clickpe.ai").replace(/\/+$/, "");
  const previewCustomerPortalUrl = `${customerBase}/?agent_id=${encodeURIComponent(agentId)}`;
  const primaryColor = useCustomTheme ? (themeColor || "#3A53A5") : "#3A53A5";
  const accentColorVal = useCustomTheme ? (accentColor || "#FCD00F") : "#FCD00F";
  const brandName = companyName || "ClickPe";

  /* -------------------------------------------------------------
     RENDER PHONE MOCKUP SHELL
     ------------------------------------------------------------- */
  const renderPhoneMockup = (title: string, screenContent: React.ReactNode) => (
    <div style={phoneWrapperStyle} key={title}>
      <div style={phoneLabelRowStyle}>
        <span style={{ fontWeight: 700, color: "#0f172a", fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
          <Smartphone size={15} color={primaryColor} />
          {title}
        </span>
        <span style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>Exact Customer View</span>
      </div>

      {/* Outer Device Bezel with Realistic Chassis & Hardware Buttons */}
      <div style={phoneBezelStyle}>
        {/* Hardware Action Button (Left Top) */}
        <div style={actionBtnStyle} />
        {/* Hardware Volume Up (Left Middle) */}
        <div style={volUpBtnStyle} />
        {/* Hardware Volume Down (Left Bottom) */}
        <div style={volDownBtnStyle} />
        {/* Hardware Power Button (Right) */}
        <div style={powerBtnStyle} />

        {/* Device Screen Glass */}
        <div style={phoneScreenStyle}>
          {/* Authentic iOS Status Bar (Clean Vectors, Dynamic Island, NO emojis) */}
          <div style={statusBarRowStyle}>
            {/* Left: Clock */}
            <span style={statusTimeStyle}>9:41</span>

            {/* Center: Dynamic Island Pill */}
            <div style={dynamicIslandStyle}>
              {/* Proximity / Mic dot */}
              <div style={sensorDotStyle} />
              {/* Front Camera Lens with anti-reflective coating reflection */}
              <div style={cameraLensStyle}>
                <div style={lensReflectionStyle} />
              </div>
            </div>

            {/* Right: Cellular, Wi-Fi, Battery Status (Pure SVGs) */}
            <div style={statusIconsRowStyle}>
              {/* Cellular 4-Bars SVG */}
              <svg width="15" height="11" viewBox="0 0 17 12" fill="none" style={{ display: "block" }}>
                <rect x="0" y="8.5" width="2.5" height="3.5" rx="0.8" fill="#0F172A" />
                <rect x="4.5" y="6" width="2.5" height="6" rx="0.8" fill="#0F172A" />
                <rect x="9" y="3.5" width="2.5" height="8.5" rx="0.8" fill="#0F172A" />
                <rect x="13.5" y="0.5" width="2.5" height="11.5" rx="0.8" fill="#0F172A" />
              </svg>

              {/* Wi-Fi 3-arcs SVG */}
              <svg width="14" height="11" viewBox="0 0 16 12" fill="none" style={{ display: "block" }}>
                <path
                  d="M8 9.5a1.25 1.25 0 100 2.5 1.25 1.25 0 000-2.5zM3.8 6.9a5.95 5.95 0 018.4 0 .75.75 0 001.06-1.06 7.45 7.45 0 00-10.52 0 .75.75 0 001.06 1.06zM1.2 4.3a9.65 9.65 0 0113.6 0 .75.75 0 101.06-1.06 11.15 11.15 0 00-15.72 0 .75.75 0 101.06 1.06z"
                  fill="#0F172A"
                />
              </svg>

              {/* Battery Indicator SVG */}
              <svg width="22" height="11" viewBox="0 0 24 12" fill="none" style={{ display: "block" }}>
                <rect x="0.5" y="0.5" width="20" height="11" rx="3.5" stroke="#0F172A" strokeWidth="1" />
                <rect x="2.5" y="2.5" width="14" height="7" rx="1.8" fill="#0F172A" />
                <path d="M21.5 4C22.3 4 23 4.7 23 5.5v1c0 .8-.7 1.5-1.5 1.5V4z" fill="#0F172A" />
              </svg>
            </div>
          </div>

          {/* Scrollable Screen Content */}
          <div style={phoneContentScrollStyle}>
            {screenContent}
          </div>

          {/* Bottom Home Indicator */}
          <div style={homeIndicatorContainerStyle}>
            <div style={homeIndicatorStyle} />
          </div>
        </div>
      </div>
    </div>
  );

  /* -------------------------------------------------------------
     EXACT RECREATION: CUSTOMER WEB APP LOGIN SCREEN (/login)
     ------------------------------------------------------------- */
  const renderCustomerLoginScreen = () => (
    <div style={{ backgroundColor: "#ffffff", minHeight: "100%", display: "flex", flexDirection: "column" }}>
      {/* Top Sticky Header (Exact Header.tsx from customer_web_app) */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 20,
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(12px)",
          borderBottom: "1px solid rgba(226, 232, 240, 0.8)",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.03)",
          padding: "8px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: 48,
          boxSizing: "border-box",
        }}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={brandName}
              style={{ maxHeight: 34, maxWidth: 120, objectFit: "contain" }}
            />
          ) : (
            <img
              src="/logo2.png"
              alt="ClickPe"
              style={{ maxHeight: 34, maxWidth: 120, objectFit: "contain" }}
            />
          )}
        </div>

        {/* Download App button if default ClickPe, hidden if partner whitelabel */}
        {!useCustomTheme && !companyName && (
          <div
            style={{
              padding: "5px 12px",
              borderRadius: 999,
              background: "linear-gradient(to right, #3B82F6, #2563EB)",
              color: "#ffffff",
              fontSize: 11,
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              gap: 5,
              boxShadow: "0 2px 8px rgba(59, 130, 246, 0.25)",
            }}
          >
            <span>Download App</span>
          </div>
        )}
      </div>

      <div style={{ padding: "8px 18px 24px 18px", flex: 1, display: "flex", flexDirection: "column" }}>
        {/* --- ORBITAL GRAPHICS HEADER AREA (Exact Login.tsx) --- */}
        <div
          style={{
            height: 200,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            margin: "8px 0 16px 0",
          }}
        >
          {/* Outer Orbit Circle */}
          <div
            style={{
              position: "absolute",
              width: 190,
              height: 190,
              borderRadius: "50%",
              border: "1px solid #E2E8F0",
              pointerEvents: "none",
            }}
          />
          {/* Inner Orbit Circle */}
          <div
            style={{
              position: "absolute",
              width: 126,
              height: 126,
              borderRadius: "50%",
              border: "1px solid #E2E8F0",
              pointerEvents: "none",
            }}
          />
          {/* Floating Decorative Color Dots */}
          <div
            style={{
              position: "absolute",
              top: "15%",
              left: "24%",
              width: 6,
              height: 6,
              borderRadius: "50%",
              backgroundColor: primaryColor,
              opacity: 0.6,
            }}
          />
          <div
            style={{
              position: "absolute",
              bottom: "15%",
              left: "30%",
              width: 6,
              height: 6,
              borderRadius: "50%",
              backgroundColor: "#10B981",
              opacity: 0.6,
            }}
          />
          <div
            style={{
              position: "absolute",
              top: "35%",
              right: "22%",
              width: 6,
              height: 6,
              borderRadius: "50%",
              backgroundColor: accentColorVal || "#60A5FA",
              opacity: 0.6,
            }}
          />

          {/* Center Elevated Graphic Container */}
          <div
            style={{
              width: 96,
              height: 96,
              borderRadius: 24,
              backgroundColor: "#ffffff",
              boxShadow: "0 10px 25px rgba(0, 0, 0, 0.08)",
              border: "1px solid rgba(226, 232, 240, 0.7)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 12,
              zIndex: 5,
            }}
          >
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={brandName}
                style={{ maxHeight: 54, maxWidth: 54, objectFit: "contain" }}
              />
            ) : (
              <img
                src="/logo2.png"
                alt="ClickPe"
                style={{ maxHeight: 54, maxWidth: 54, objectFit: "contain" }}
              />
            )}
          </div>
        </div>

        {/* --- HEADER TITLE & SUBTITLE --- */}
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <h2
            style={{
              fontSize: 26,
              fontWeight: 800,
              color: "#0F172A",
              margin: "0 0 6px 0",
              letterSpacing: "-0.3px",
            }}
          >
            Welcome back
          </h2>
          <p style={{ fontSize: 13, color: "#64748B", margin: 0, lineHeight: 1.4 }}>
            Login to access your dashboard and financial products
          </p>
        </div>

        {/* --- PHONE INPUT (Exact FormControl from Login.tsx) --- */}
        <div
          style={{
            height: 60,
            backgroundColor: "#FAFAFA",
            borderRadius: 16,
            border: "1px solid #E2E8F0",
            padding: "0 16px",
            display: "flex",
            alignItems: "center",
            boxSizing: "border-box",
            marginBottom: 16,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              paddingRight: 12,
              borderRight: "1px solid #E2E8F0",
              fontSize: 16,
              fontWeight: 700,
              color: "#0F172A",
            }}
          >
            <span>+91</span>
            <ChevronDown size={15} color="#64748B" />
          </div>
          <div
            style={{
              paddingLeft: 14,
              fontSize: 16,
              fontWeight: 600,
              color: "#0F172A",
              letterSpacing: "0.2px",
            }}
          >
            98765 43210
          </div>
        </div>

        {/* --- GET OTP BUTTON --- */}
        <button
          type="button"
          style={{
            width: "100%",
            height: 56,
            borderRadius: 14,
            backgroundColor: primaryColor,
            color: "#ffffff",
            fontSize: 16,
            fontWeight: 800,
            border: "none",
            boxShadow: `0 6px 16px ${primaryColor}40`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            cursor: "pointer",
          }}
        >
          <span>Get OTP</span>
          <ArrowRight size={18} />
        </button>

        {/* --- TRUST REASSURANCE --- */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
            marginTop: 14,
          }}
        >
          <Shield size={15} color={primaryColor} />
          <span style={{ fontSize: 12, color: "#64748B", fontWeight: 500 }}>
            Secure. Private. Trusted by millions.
          </span>
        </div>

        {/* --- PCI DSS COMPLIANT BADGE --- */}
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", marginTop: 10 }}>
          <img
            src="/pcidss.png"
            alt="PCI DSS Compliant"
            style={{ maxHeight: 28, objectFit: "contain", filter: "grayscale(100%)", opacity: 0.65 }}
          />
        </div>

        {/* --- BOTTOM 3D LOCK GRAPHIC (Exact Login.tsx:1384-1388) --- */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginTop: 16,
            height: 150,
            overflow: "hidden",
          }}
        >
          <img
            src="/lock.png"
            alt="Secure Lock"
            style={{ maxHeight: 160, maxWidth: 300, objectFit: "contain" }}
          />
        </div>
      </div>
    </div>
  );

  /* -------------------------------------------------------------
     EXACT RECREATION: CUSTOMER WEB APP HOME SCREEN (/home)
     (Financial Health Hero Removed as explicitly requested)
     ------------------------------------------------------------- */
  const renderCustomerHomeScreen = () => (
    <div style={{ backgroundColor: "#FAFAFC", minHeight: "100%", display: "flex", flexDirection: "column" }}>
      {/* Top Sticky FlowHeader Navbar (Exact FlowHeader.tsx from customer_web_app) */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 20,
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(12px)",
          borderBottom: "1px solid rgba(226, 232, 240, 0.8)",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.03)",
          padding: "8px 12px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: 48,
          boxSizing: "border-box",
        }}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={brandName}
              style={{ maxHeight: 32, maxWidth: 100, objectFit: "contain" }}
            />
          ) : (
            <img
              src="/logo2.png"
              alt="ClickPe"
              style={{ maxHeight: 32, maxWidth: 100, objectFit: "contain" }}
            />
          )}
        </div>

        {/* Actions: Home, Profile, Logout */}
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <div
            style={{
              height: 32,
              padding: "0 8px",
              borderRadius: 10,
              backgroundColor: `${primaryColor}14`,
              color: primaryColor,
              fontSize: 11,
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <Home size={13} color={primaryColor} />
            <span>Home</span>
          </div>

          <div
            style={{
              height: 32,
              padding: "0 8px",
              borderRadius: 10,
              backgroundColor: "transparent",
              color: "#334155",
              fontSize: 11,
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <User size={13} color="#475569" />
            <span>Profile</span>
          </div>

          <div
            style={{
              height: 32,
              padding: "0 8px",
              borderRadius: 10,
              border: "1px solid #E2E8F0",
              color: "#64748B",
              fontSize: 11,
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <LogOut size={13} color="#64748B" />
            <span>Logout</span>
          </div>
        </div>
      </div>

      <div style={{ padding: "14px 12px 28px 12px", flex: 1 }}>
        {/* QUICK ELIGIBILITY CHECK BANNER (HomeScreenPage.tsx:110-151) */}
        <div
          style={{
            backgroundColor: "#ffffff",
            borderRadius: 20,
            padding: "14px 16px",
            border: "1px solid #E2E8F0",
            boxShadow: "0 4px 16px rgba(15, 23, 42, 0.04)",
            position: "relative",
            overflow: "hidden",
            cursor: "pointer",
            marginBottom: 16,
            minHeight: 90,
            background: `linear-gradient(to right, ${primaryColor}14 0%, #ffffff 100%)`,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, position: "relative", zIndex: 2 }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 800, color: "#0F172A", lineHeight: 1.2 }}>
                Check Eligibility
              </div>
              <div style={{ fontSize: 11, color: "#64748B", marginTop: 2 }}>
                See your pre-qualified offers
              </div>
              <span
                style={{
                  display: "inline-block",
                  backgroundColor: `${primaryColor}18`,
                  color: primaryColor,
                  fontSize: 9,
                  fontWeight: 800,
                  borderRadius: 999,
                  padding: "2px 7px",
                  marginTop: 6,
                }}
              >
                QUICK CHECK
              </span>
            </div>
            <div
              style={{
                padding: 6,
                backgroundColor: `${primaryColor}18`,
                borderRadius: "50%",
                color: primaryColor,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <ArrowRight size={14} />
            </div>
          </div>
        </div>

        {/* EXPLORE PRODUCTS BENTO GRID (ExploreProductsBento.tsx) */}
        <div style={{ marginBottom: 18 }}>
          {/* Header Row */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <span style={{ fontSize: 18, fontWeight: 800, color: "#0F172A" }}>
              Explore Products
            </span>
            <span style={{ fontSize: 14, fontWeight: 700, color: primaryColor, display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
              <span>See all</span>
              <ArrowRight size={14} />
            </span>
          </div>

          {/* ROW 1: 2 Equal Columns (Personal Loan & Business Loan) */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
            {/* Card 1: Personal Loan */}
            <div
              style={{
                backgroundColor: "#ffffff",
                borderRadius: 20,
                padding: "14px 12px",
                border: "1px solid #E2E8F0",
                boxShadow: "0 4px 16px rgba(15, 23, 42, 0.04)",
                position: "relative",
                overflow: "hidden",
                minHeight: 95,
              }}
            >
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background: `linear-gradient(to right, ${primaryColor}14 0%, rgba(255, 255, 255, 0) 100%)`,
                  pointerEvents: "none",
                }}
              />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6, position: "relative", zIndex: 2 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, flex: 1 }}>
                  <img
                    src="/assets/bag.png"
                    alt="Personal Loan Bag"
                    style={{ width: 44, height: 44, objectFit: "contain", flexShrink: 0 }}
                  />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: "#0F172A", lineHeight: 1.2 }}>
                      Personal Loan
                    </div>
                    <div style={{ fontSize: 10, color: "#64748B", marginTop: 2 }}>
                      Up to ₹5 Lakhs
                    </div>
                    <span
                      style={{
                        display: "inline-block",
                        backgroundColor: `${primaryColor}18`,
                        color: primaryColor,
                        fontSize: 8,
                        fontWeight: 800,
                        borderRadius: 999,
                        padding: "1px 6px",
                        marginTop: 4,
                      }}
                    >
                      CASHBACK
                    </span>
                  </div>
                </div>
                <div
                  style={{
                    padding: 5,
                    backgroundColor: `${primaryColor}18`,
                    borderRadius: "50%",
                    color: primaryColor,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <ArrowRight size={12} />
                </div>
              </div>
            </div>

            {/* Card 2: Business Loan */}
            <div
              style={{
                backgroundColor: "#ffffff",
                borderRadius: 20,
                padding: "14px 12px",
                border: "1px solid #E2E8F0",
                boxShadow: "0 4px 16px rgba(15, 23, 42, 0.04)",
                position: "relative",
                overflow: "hidden",
                minHeight: 95,
              }}
            >
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background: "linear-gradient(to right, rgba(16, 185, 129, 0.12) 0%, rgba(255, 255, 255, 0) 100%)",
                  pointerEvents: "none",
                }}
              />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6, position: "relative", zIndex: 2 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, flex: 1 }}>
                  <img
                    src="/assets/building.png"
                    alt="Business Building"
                    style={{ width: 44, height: 44, objectFit: "contain", flexShrink: 0 }}
                  />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: "#0F172A", lineHeight: 1.2 }}>
                      Business Loan
                    </div>
                    <div style={{ fontSize: 10, color: "#64748B", marginTop: 2 }}>
                      Collateral-free
                    </div>
                    <span
                      style={{
                        display: "inline-block",
                        backgroundColor: "#ECFDF5",
                        color: "#10B981",
                        fontSize: 8,
                        fontWeight: 800,
                        borderRadius: 999,
                        padding: "1px 6px",
                        marginTop: 4,
                      }}
                    >
                      ZERO FEES
                    </span>
                  </div>
                </div>
                <div
                  style={{
                    padding: 5,
                    backgroundColor: "#ECFDF5",
                    borderRadius: "50%",
                    color: "#10B981",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <ArrowRight size={12} />
                </div>
              </div>
            </div>
          </div>

          {/* ROW 2: 3 Columns (Credit Cards, Savings Accounts, Demat Accounts) */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
            {/* Card 3: Credit Cards */}
            <div
              style={{
                backgroundColor: "#ffffff",
                borderRadius: 20,
                padding: "14px 10px",
                border: "1px solid #E2E8F0",
                boxShadow: "0 4px 16px rgba(15, 23, 42, 0.04)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                <img
                  src="/assets/creditcard.png"
                  alt="Credit Card 3D"
                  style={{ width: 46, height: 46, objectFit: "contain" }}
                />
                <div style={{ padding: 4, backgroundColor: `${primaryColor}18`, borderRadius: "50%", color: primaryColor }}>
                  <ArrowRight size={11} />
                </div>
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 800, color: "#0F172A", lineHeight: 1.2 }}>Credit Cards</div>
                <div style={{ fontSize: 10, color: "#64748B", marginTop: 2, marginBottom: 5 }}>Up to ₹5,000 OFF</div>
                <span style={{ fontSize: 9, fontWeight: 800, backgroundColor: `${primaryColor}18`, color: primaryColor, borderRadius: 999, padding: "2px 6px", display: "inline-block" }}>
                  ₹500 OFF
                </span>
              </div>
            </div>

            {/* Card 4: Savings Accounts */}
            <div
              style={{
                backgroundColor: "#ffffff",
                borderRadius: 20,
                padding: "14px 10px",
                border: "1px solid #E2E8F0",
                boxShadow: "0 4px 16px rgba(15, 23, 42, 0.04)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                <img
                  src="/assets/wallet.png"
                  alt="Wallet 3D"
                  style={{ width: 46, height: 46, objectFit: "contain" }}
                />
                <div style={{ padding: 4, backgroundColor: "#ECFDF5", borderRadius: "50%", color: "#10B981" }}>
                  <ArrowRight size={11} />
                </div>
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 800, color: "#0F172A", lineHeight: 1.2 }}>Savings</div>
                <div style={{ fontSize: 10, color: "#64748B", marginTop: 2, marginBottom: 5 }}>Instant digital</div>
                <span style={{ fontSize: 9, fontWeight: 800, backgroundColor: "#ECFDF5", color: "#10B981", borderRadius: 999, padding: "2px 6px", display: "inline-block" }}>
                  HIGH INT
                </span>
              </div>
            </div>

            {/* Card 5: Demat Account */}
            <div
              style={{
                backgroundColor: "#ffffff",
                borderRadius: 20,
                padding: "14px 10px",
                border: "1px solid #E2E8F0",
                boxShadow: "0 4px 16px rgba(15, 23, 42, 0.04)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                <img
                  src="/assets/candlestick-chart.png"
                  alt="Candlestick Chart 3D"
                  style={{ width: 46, height: 46, objectFit: "contain" }}
                />
                <div style={{ padding: 4, backgroundColor: `${primaryColor}18`, borderRadius: "50%", color: primaryColor }}>
                  <ArrowRight size={11} />
                </div>
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 800, color: "#0F172A", lineHeight: 1.2 }}>Demat</div>
                <div style={{ fontSize: 10, color: "#64748B", marginTop: 2, marginBottom: 5 }}>Zero AMC</div>
                <span style={{ fontSize: 9, fontWeight: 800, backgroundColor: `${primaryColor}18`, color: primaryColor, borderRadius: 999, padding: "2px 6px", display: "inline-block" }}>
                  FREE
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* RECOMMENDED FOR YOU (Exact ProductCard from ProductCatalog.tsx) */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 800, color: "#0F172A" }}>Recommended for You</div>
              <div style={{ fontSize: 11, color: "#64748B" }}>Handpicked pre-qualified offers for your profile</div>
            </div>
            <span style={{ fontSize: 12, fontWeight: 700, color: primaryColor, cursor: "pointer" }}>See all</span>
          </div>

          {/* Product Card with Floating Green Pre-Qualified Badge */}
          <div
            style={{
              position: "relative",
              background: "linear-gradient(135deg, #F0FDF4 0%, #FFFFFF 100%)",
              borderRadius: 20,
              padding: 16,
              border: "2px solid #10B981",
              boxShadow: "0 6px 20px rgba(16, 185, 129, 0.18)",
              marginTop: 12,
            }}
          >
            {/* FLOATING GREEN PRE-QUALIFIED BADGE */}
            <div
              style={{
                position: "absolute",
                top: -12,
                left: 16,
                backgroundColor: "#10B981",
                color: "#ffffff",
                padding: "3px 10px",
                borderRadius: 999,
                display: "flex",
                alignItems: "center",
                gap: 5,
                boxShadow: "0 4px 10px rgba(16, 185, 129, 0.35)",
                fontSize: 9,
                fontWeight: 900,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                zIndex: 10,
              }}
            >
              <CheckCircle size={11} color="#ffffff" />
              <span>Pre-Qualified</span>
            </div>

            {/* Top Row: Lender, Category badge, Share */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, marginTop: 2 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 10,
                    backgroundColor: "#ffffff",
                    border: "1px solid #E2E8F0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                  }}
                >
                  <img
                    src="/assets/ClickPe_Logo.png"
                    alt="Lender"
                    style={{ maxHeight: 20, maxWidth: 24, objectFit: "contain" }}
                  />
                </div>
                <span
                  style={{
                    padding: "2px 7px",
                    borderRadius: 6,
                    backgroundColor: primaryColor,
                    color: "#ffffff",
                    fontSize: 9,
                    fontWeight: 800,
                    textTransform: "uppercase",
                  }}
                >
                  Personal Loan
                </span>
                <span style={{ fontSize: 10, color: "#64748B", fontWeight: 500 }}>
                  • By Muthoot Finance
                </span>
              </div>

              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  backgroundColor: "#F8FAFC",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px solid #E2E8F0",
                }}
              >
                <Share2 size={12} color="#64748B" />
              </div>
            </div>

            {/* Main Row: Details + 3D Bag Asset */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: "#0F172A", marginBottom: 2 }}>
                  Muthoot Personal Loan
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 8 }}>
                  <Shield size={11} color={primaryColor} />
                  <span style={{ fontSize: 10, color: "#64748B", fontWeight: 500 }}>
                    Instant Disbursal in 10 Mins • Zero Paperwork
                  </span>
                </div>

                {/* Stats Row */}
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ backgroundColor: "#F8FAFC", padding: "6px 8px", borderRadius: 8, border: "1px solid #E2E8F0" }}>
                    <div style={{ fontSize: 9, color: "#64748B", fontWeight: 600 }}>Tenure</div>
                    <div style={{ fontSize: 13, fontWeight: 800, color: primaryColor }}>Up to 60 Mo</div>
                  </div>

                  <div style={{ backgroundColor: "#F8FAFC", padding: "6px 8px", borderRadius: 8, border: "1px solid #E2E8F0" }}>
                    <div style={{ fontSize: 9, color: "#64748B", fontWeight: 600 }}>Approval Odds</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 1 }}>
                      <img src="/m_exc.svg" alt="Meter" style={{ height: 12, width: 20, objectFit: "contain" }} />
                      <span style={{ fontSize: 11, fontWeight: 800, color: "#10B981" }}>Excellent</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* 3D Asset Bag Image */}
              <div style={{ width: 64, height: 64, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <img
                  src="/assets/bag.png"
                  alt="Personal Loan Bag 3D"
                  style={{ width: 60, height: 60, objectFit: "contain" }}
                />
              </div>
            </div>

            {/* Bottom Row: Precheck Pill / Apply Now */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: 8, borderTop: "1px solid #E2E8F0" }}>
              <span
                style={{
                  backgroundColor: "#ECFDF5",
                  color: "#10B981",
                  padding: "3px 8px",
                  borderRadius: 999,
                  fontSize: 10,
                  fontWeight: 700,
                }}
              >
                Pre-Qualified
              </span>
              <button
                type="button"
                style={{
                  backgroundColor: primaryColor,
                  color: "#ffffff",
                  border: "none",
                  borderRadius: 10,
                  padding: "7px 16px",
                  fontSize: 12,
                  fontWeight: 800,
                  cursor: "pointer",
                  boxShadow: `0 3px 10px ${primaryColor}35`,
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                }}
              >
                <span>Apply Now</span>
                <ArrowRight size={13} />
              </button>
            </div>
          </div>

          {/* Carousel Pagination Indicator Dots */}
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 6, marginTop: 10 }}>
            <div style={{ width: 24, height: 6, borderRadius: 999, backgroundColor: primaryColor }} />
            <div style={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: "#CBD5E1" }} />
            <div style={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: "#CBD5E1" }} />
          </div>
        </div>

        {/* EXACT TRUST FOOTER (TrustFooter.tsx) */}
        <div style={{ width: "100%", paddingTop: 14, paddingBottom: 10, color: "#64748B", textAlign: "center" }}>
          <div style={{ height: 1, backgroundColor: "#E2E8F0", marginBottom: 12 }} />
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 6 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700, color: primaryColor }}>
              <Shield size={13} color={primaryColor} />
              <span>RBI Regulated Partners</span>
            </div>
            <span style={{ fontSize: 11, color: "#CBD5E1" }}>•</span>
            <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700, color: "#8B5CF6" }}>
              <CheckCircle2 size={13} color="#8B5CF6" />
              <span>Zero Paperwork</span>
            </div>
          </div>
          <p style={{ fontSize: 10, color: "#94A3B8", margin: 0, lineHeight: 1.4 }}>
            NeoFirst Technologies India Pvt. Ltd. ({brandName}) acts as a credit facilitator and DSA partner for RBI registered Banks & NBFCs.
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <MainLayout>
      <div style={pageContainerStyle}>
        {/* Page Header */}
        <div style={pageHeaderStyle}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div style={buildingIconBadgeStyle}>
                <Building2 size={26} color="#1565d8" strokeWidth={1.8} />
              </div>
              <div>
                <h1 style={pageTitleStyle}>White Labelling Configuration</h1>
                <p style={pageSubtitleStyle}>
                  Deliver a fully branded loan portal experience to your customers.
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              style={{
                ...saveBtnStyle,
                opacity: saving ? 0.7 : 1,
                cursor: saving ? "not-allowed" : "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              {saving ? "Saving Configuration..." : "Save White Label Config"}
            </button>
          </div>
        </div>

        {loading ? (
          <div style={{ ...cardContainerStyle, padding: "80px 0", textAlign: "center", color: "#94a3b8" }}>
            <div style={spinnerStyle} />
            <p style={{ marginTop: 14, fontSize: 14 }}>Loading configuration...</p>
          </div>
        ) : (
          <div style={splitLayoutStyle}>
            {/* LEFT: Settings */}
            <div style={settingsColumnStyle}>
              {errorMessage && (
                <div style={errorBannerStyle}>
                  <AlertCircle size={18} color="#b91c1c" />
                  <span>{errorMessage}</span>
                </div>
              )}

              {successMessage && (
                <div style={successBannerStyle}>
                  <CheckCircle2 size={18} color="#15803d" />
                  <span>{successMessage}</span>
                </div>
              )}

              {/* Enable White Labelling */}
              <div style={enableCardStyle}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: "#0f172a", display: "flex", alignItems: "center", gap: 8 }}>
                    Enable White Labelling
                    <span
                      style={{
                        ...statusPillStyle,
                        background: isActive ? "#dcfce7" : "#f1f5f9",
                        color: isActive ? "#15803d" : "#64748b",
                      }}
                    >
                      {isActive ? "Enabled" : "Disabled"}
                    </span>
                  </div>
                  <div style={{ fontSize: 13, color: "#64748b", marginTop: 2 }}>
                    Apply your custom branding, logo, and theme to customer portal & links.
                  </div>
                </div>
                <label style={switchLabelStyle}>
                  <input
                    type="checkbox"
                    aria-label="Enable white labelling"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    style={{ display: "none" }}
                  />
                  <div style={{ ...switchTrackStyle, backgroundColor: isActive ? "#1565d8" : "#cbd5e1" }}>
                    <div style={{ ...switchThumbStyle, transform: isActive ? "translateX(20px)" : "translateX(2px)" }} />
                  </div>
                </label>
              </div>

              {/* Section 1: Brand Identity */}
              <section style={sectionCardStyle}>
                <div style={sectionHeaderStyle}>
                  <div style={sectionIconStyle}>
                    <Building2 size={18} color="#1565d8" />
                  </div>
                  <div>
                    <div style={sectionTitleStyle}>Brand Identity</div>
                    <div style={sectionSubtitleStyle}>The name and logo your customers will see</div>
                  </div>
                </div>

                <div style={sectionBodyStyle}>
                  <div style={formGroupStyle}>
                    <label style={labelStyle}>
                      Company / Brand Name <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Vishtra Money, Coupl, Acme Loans"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      style={inputStyle}
                    />
                    <p style={helpTextStyle}>
                      This name replaces ClickPe across customer-facing pages and shareable links.
                    </p>
                  </div>

                  <div style={{ ...formGroupStyle, marginBottom: 0 }}>
                    <label style={labelStyle}>
                      Brand Logo <span style={{ color: "#ef4444" }}>*</span>
                    </label>

                    <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                      <button
                        type="button"
                        onClick={() => setLogoInputType("upload")}
                        style={{
                          ...logoToggleBtnStyle,
                          background: logoInputType === "upload" ? "#eff6ff" : "#ffffff",
                          borderColor: logoInputType === "upload" ? "#1565d8" : "#e2e8f0",
                          color: logoInputType === "upload" ? "#1565d8" : "#64748b",
                        }}
                      >
                        <Upload size={14} />
                        <span>Upload File</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setLogoInputType("url")}
                        style={{
                          ...logoToggleBtnStyle,
                          background: logoInputType === "url" ? "#eff6ff" : "#ffffff",
                          borderColor: logoInputType === "url" ? "#1565d8" : "#e2e8f0",
                          color: logoInputType === "url" ? "#1565d8" : "#64748b",
                        }}
                      >
                        <Image size={14} />
                        <span>Logo URL</span>
                      </button>
                    </div>

                    <div style={{ display: "flex", gap: 14, alignItems: "stretch" }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {logoInputType === "upload" ? (
                          <div style={uploadDashedBoxStyle} onClick={() => fileInputRef.current?.click()}>
                            <input
                              ref={fileInputRef}
                              type="file"
                              accept="image/*"
                              onChange={handleFileUpload}
                              style={{ display: "none" }}
                            />
                            <Upload size={22} color="#94a3b8" />
                            <div style={{ fontSize: 13, fontWeight: 600, color: "#334155", marginTop: 6 }}>
                              {uploading ? "Processing..." : logoUrl ? "Click to replace logo" : "Click to browse and upload logo"}
                            </div>
                            <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>
                              PNG, SVG, JPG, WebP up to 2MB · transparent background recommended
                            </div>
                          </div>
                        ) : (
                          <input
                            type="url"
                            placeholder="https://yourdomain.com/logo.png"
                            value={logoUrl}
                            onChange={(e) => setLogoUrl(e.target.value)}
                            style={inputStyle}
                          />
                        )}
                      </div>

                      {logoUrl && (
                        <div style={checkeredBoxStyle} title="Logo preview">
                          <img
                            src={logoUrl}
                            alt="Logo preview"
                            style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                            onError={(e) => {
                              (e.currentTarget as HTMLImageElement).style.display = "none";
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </section>

              {/* Section 2: Theme & Colors */}
              <section style={sectionCardStyle}>
                <div style={sectionHeaderStyle}>
                  <div style={sectionIconStyle}>
                    <Palette size={18} color="#1565d8" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={sectionTitleStyle}>Theme & Colors</div>
                    <div style={sectionSubtitleStyle}>
                      {useCustomTheme ? "Custom palette applied to your customer app" : "Using the standard ClickPe palette"}
                    </div>
                  </div>
                  <label style={switchLabelStyle}>
                    <input
                      type="checkbox"
                      aria-label="Use custom theme"
                      checked={useCustomTheme}
                      onChange={(e) => setUseCustomTheme(e.target.checked)}
                      style={{ display: "none" }}
                    />
                    <div style={{ ...switchTrackStyle, backgroundColor: useCustomTheme ? "#1565d8" : "#cbd5e1" }}>
                      <div style={{ ...switchThumbStyle, transform: useCustomTheme ? "translateX(20px)" : "translateX(2px)" }} />
                    </div>
                  </label>
                </div>

                <div style={sectionBodyStyle}>
                  {useCustomTheme ? (
                    <>
                      <label style={{ ...labelStyle, marginBottom: 8 }}>Preset Color Schemes</label>
                      <div style={presetGridStyle}>
                        {PRESET_THEMES.map((preset) => {
                          const isCurrent =
                            themeColor.toLowerCase() === preset.primary.toLowerCase() &&
                            accentColor.toLowerCase() === preset.accent.toLowerCase();
                          return (
                            <button
                              key={preset.name}
                              type="button"
                              onClick={() => {
                                setThemeColor(preset.primary);
                                setAccentColor(preset.accent);
                              }}
                              style={{
                                ...presetCardStyle,
                                borderColor: isCurrent ? "#1565d8" : "#e2e8f0",
                                background: isCurrent ? "#eff6ff" : "#ffffff",
                              }}
                            >
                              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                                <div style={{ ...colorSwatchStyle, backgroundColor: preset.primary }} />
                                <div style={{ ...colorSwatchStyle, backgroundColor: preset.accent }} />
                              </div>
                              <span style={{ fontSize: 12, fontWeight: 600, color: isCurrent ? "#1565d8" : "#334155", flex: 1 }}>
                                {preset.name}
                              </span>
                              {isCurrent && <CheckCircle size={14} color="#1565d8" />}
                            </button>
                          );
                        })}
                      </div>

                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginTop: 18 }}>
                        <div>
                          <label style={labelStyle}>Primary Color</label>
                          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            <input
                              type="color"
                              value={themeColor}
                              onChange={(e) => setThemeColor(e.target.value)}
                              style={colorInputStyle}
                            />
                            <input
                              type="text"
                              value={themeColor}
                              onChange={(e) => setThemeColor(e.target.value)}
                              style={hexInputStyle}
                            />
                          </div>
                          <span style={helpTextStyle}>Buttons, headers, active highlights</span>
                        </div>
                        <div>
                          <label style={labelStyle}>Accent Color</label>
                          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            <input
                              type="color"
                              value={accentColor}
                              onChange={(e) => setAccentColor(e.target.value)}
                              style={colorInputStyle}
                            />
                            <input
                              type="text"
                              value={accentColor}
                              onChange={(e) => setAccentColor(e.target.value)}
                              style={hexInputStyle}
                            />
                          </div>
                          <span style={helpTextStyle}>Badges, tags, promotional callouts</span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{ display: "flex", gap: 4 }}>
                        <div style={{ ...colorSwatchStyle, width: 28, height: 28, backgroundColor: "#3A53A5" }} />
                        <div style={{ ...colorSwatchStyle, width: 28, height: 28, backgroundColor: "#FCD00F" }} />
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13, color: "#334155" }}>
                          ClickPe default · #3A53A5 / #FCD00F
                        </div>
                        <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                          Turn on the switch to pick your own brand colors.
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </section>

              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  style={{ ...saveBtnStyle, opacity: saving ? 0.7 : 1, cursor: saving ? "not-allowed" : "pointer" }}
                >
                  {saving ? "Saving Configuration..." : "Save White Label Config"}
                </button>
              </div>
            </div>

            {/* RIGHT: Live Preview (sticky, updates as settings change) */}
            <aside style={previewColumnStyle}>
              <div style={previewPanelStyle}>
                <div style={previewHeaderStyle}>
                  <div>
                    <div style={sectionTitleStyle}>Live Preview</div>
                    <div style={{ ...sectionSubtitleStyle, display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={liveDotStyle} />
                      Updates as you edit
                    </div>
                  </div>
                  <a
                    href={previewCustomerPortalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={portalLinkBtnStyle}
                    title="Open the live customer app (shows saved settings)"
                  >
                    <span>Open live app</span>
                    <ExternalLink size={13} />
                  </a>
                </div>

                <div style={previewModeSwitcherStyle}>
                  {([
                    { id: "login", label: "Login Screen", icon: Shield },
                    { id: "home", label: "Home Screen", icon: Home },
                  ] as const).map(({ id, label, icon: Icon }) => {
                    const selected = previewScreen === id;
                    return (
                      <button
                        key={id}
                        type="button"
                        onClick={() => setPreviewScreen(id)}
                        style={{
                          ...previewModeBtnStyle,
                          background: selected ? "#ffffff" : "transparent",
                          color: selected ? "#1565d8" : "#64748b",
                          fontWeight: selected ? 700 : 500,
                          boxShadow: selected ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                        }}
                      >
                        <Icon size={14} />
                        <span>{label}</span>
                      </button>
                    );
                  })}
                </div>

                {previewScreen === "login"
                  ? renderPhoneMockup("Customer Web App — Login", renderCustomerLoginScreen())
                  : renderPhoneMockup("Customer Web App — Home", renderCustomerHomeScreen())}
              </div>
            </aside>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

// Styles
const pageContainerStyle: React.CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  paddingBottom: 40,
};

const pageHeaderStyle: React.CSSProperties = {
  marginBottom: 24,
  width: "100%",
};

const pageTitleStyle: React.CSSProperties = {
  fontSize: 24,
  fontWeight: 700,
  color: "#001143",
  margin: 0,
};

const pageSubtitleStyle: React.CSSProperties = {
  fontSize: 14,
  color: "#6b7280",
  margin: "4px 0 0 0",
};

const cardContainerStyle: React.CSSProperties = {
  backgroundColor: "#ffffff",
  borderRadius: 16,
  border: "1px solid #e2e8f0",
  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
  overflow: "hidden",
  width: "100%",
  boxSizing: "border-box",
};

const buildingIconBadgeStyle: React.CSSProperties = {
  width: 48,
  height: 48,
  borderRadius: 12,
  backgroundColor: "#eff6ff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const splitLayoutStyle: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: 24,
  alignItems: "flex-start",
};

const settingsColumnStyle: React.CSSProperties = {
  flex: "1 1 520px",
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: 20,
};

const previewColumnStyle: React.CSSProperties = {
  flex: "0 1 440px",
  minWidth: 0,
  position: "sticky",
  top: 88, // fixed header (64px) + breathing room
  alignSelf: "flex-start",
};

const previewPanelStyle: React.CSSProperties = {
  ...cardContainerStyle,
  overflow: "visible",
  padding: 16,
  display: "flex",
  flexDirection: "column",
  gap: 12,
  background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
};

const previewHeaderStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
};

const liveDotStyle: React.CSSProperties = {
  width: 8,
  height: 8,
  borderRadius: "50%",
  backgroundColor: "#22c55e",
  boxShadow: "0 0 0 3px rgba(34, 197, 94, 0.2)",
};

const sectionCardStyle: React.CSSProperties = {
  ...cardContainerStyle,
};

const sectionHeaderStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "16px 20px",
  borderBottom: "1px solid #f1f5f9",
};

const sectionIconStyle: React.CSSProperties = {
  width: 36,
  height: 36,
  borderRadius: 10,
  backgroundColor: "#eff6ff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: 15,
  fontWeight: 700,
  color: "#0f172a",
};

const sectionSubtitleStyle: React.CSSProperties = {
  fontSize: 12,
  color: "#64748b",
  marginTop: 2,
};

const sectionBodyStyle: React.CSSProperties = {
  padding: 20,
};

const statusPillStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  padding: "2px 8px",
  borderRadius: 999,
};

const formGroupStyle: React.CSSProperties = {
  marginBottom: 20,
};

const labelStyle: React.CSSProperties = {
  display: "block",
  fontSize: 13,
  fontWeight: 700,
  color: "#0f172a",
  marginBottom: 6,
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "10px 14px",
  borderRadius: 8,
  border: "1px solid #cbd5e1",
  fontSize: 14,
  outline: "none",
  color: "#0f172a",
  backgroundColor: "#f8fafc",
  boxSizing: "border-box",
};

const helpTextStyle: React.CSSProperties = {
  fontSize: 12,
  color: "#64748b",
  marginTop: 5,
};

const enableCardStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 16,
  padding: "16px 20px",
  borderRadius: 16,
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
};

const switchLabelStyle: React.CSSProperties = {
  cursor: "pointer",
  display: "inline-block",
};

const switchTrackStyle: React.CSSProperties = {
  width: 44,
  height: 24,
  borderRadius: 12,
  position: "relative",
  transition: "background-color 0.2s",
};

const switchThumbStyle: React.CSSProperties = {
  width: 20,
  height: 20,
  borderRadius: "50%",
  backgroundColor: "#ffffff",
  position: "absolute",
  top: 2,
  transition: "transform 0.2s",
  boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
};

const logoToggleBtnStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "8px 14px",
  borderRadius: 8,
  border: "1px solid",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
  transition: "all 0.15s ease",
};

const uploadDashedBoxStyle: React.CSSProperties = {
  border: "2px dashed #cbd5e1",
  borderRadius: 12,
  padding: "18px 16px",
  textAlign: "center",
  cursor: "pointer",
  backgroundColor: "#f8fafc",
  transition: "border-color 0.15s ease",
};

const checkeredBoxStyle: React.CSSProperties = {
  width: 72,
  height: 72,
  borderRadius: 12,
  border: "1px solid #e2e8f0",
  background: "#f8fafc",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 6,
  flexShrink: 0,
  boxSizing: "border-box",
};

const presetGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
  gap: 10,
  marginBottom: 14,
};

const presetCardStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid",
  cursor: "pointer",
  transition: "all 0.15s ease",
  textAlign: "left",
};

const colorSwatchStyle: React.CSSProperties = {
  width: 18,
  height: 18,
  borderRadius: 6,
  border: "1px solid rgba(0,0,0,0.1)",
  flexShrink: 0,
};

const colorInputStyle: React.CSSProperties = {
  width: 36,
  height: 36,
  borderRadius: 8,
  border: "1px solid #cbd5e1",
  cursor: "pointer",
  padding: 2,
};

const hexInputStyle: React.CSSProperties = {
  flex: 1,
  padding: "8px 12px",
  borderRadius: 8,
  border: "1px solid #cbd5e1",
  fontSize: 13,
  fontFamily: "monospace",
  color: "#0f172a",
};

const previewModeSwitcherStyle: React.CSSProperties = {
  display: "flex",
  background: "#f1f5f9",
  padding: 4,
  borderRadius: 10,
  gap: 4,
};

const previewModeBtnStyle: React.CSSProperties = {
  padding: "8px 14px",
  borderRadius: 8,
  border: "none",
  fontSize: 12,
  cursor: "pointer",
  flex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
  transition: "all 0.15s ease",
};

const portalLinkBtnStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "8px 16px",
  borderRadius: 8,
  background: "#1565d8",
  color: "#ffffff",
  textDecoration: "none",
  fontSize: 12,
  fontWeight: 600,
  boxShadow: "0 2px 6px rgba(21, 101, 216, 0.25)",
};

const phoneWrapperStyle: React.CSSProperties = {
  flex: "1 1 360px",
  maxWidth: 410,
  minWidth: 0,
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  margin: "0 auto",
};

const phoneLabelRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
  padding: "0 8px 10px 8px",
};

const phoneBezelStyle: React.CSSProperties = {
  width: "100%",
  height: "min(780px, calc(100vh - 250px))",
  minHeight: 560,
  background: "linear-gradient(145deg, #1e293b 0%, #0f172a 100%)",
  borderRadius: 52,
  padding: "11px",
  boxShadow:
    "0 25px 60px -15px rgba(15, 23, 42, 0.45), 0 0 0 1px rgba(255, 255, 255, 0.12) inset, 0 8px 20px rgba(0, 0, 0, 0.3)",
  display: "flex",
  flexDirection: "column",
  position: "relative",
  boxSizing: "border-box",
};

const actionBtnStyle: React.CSSProperties = {
  position: "absolute",
  left: -3,
  top: 120,
  width: 3,
  height: 26,
  backgroundColor: "#334155",
  borderRadius: "3px 0 0 3px",
};

const volUpBtnStyle: React.CSSProperties = {
  position: "absolute",
  left: -3,
  top: 165,
  width: 3,
  height: 52,
  backgroundColor: "#334155",
  borderRadius: "3px 0 0 3px",
};

const volDownBtnStyle: React.CSSProperties = {
  position: "absolute",
  left: -3,
  top: 232,
  width: 3,
  height: 52,
  backgroundColor: "#334155",
  borderRadius: "3px 0 0 3px",
};

const powerBtnStyle: React.CSSProperties = {
  position: "absolute",
  right: -3,
  top: 180,
  width: 3,
  height: 76,
  backgroundColor: "#334155",
  borderRadius: "0 3px 3px 0",
};

const phoneScreenStyle: React.CSSProperties = {
  width: "100%",
  height: "100%",
  backgroundColor: "#ffffff",
  borderRadius: 40,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  position: "relative",
  boxShadow: "inset 0 0 0 1px rgba(0, 0, 0, 0.08)",
};

const statusBarRowStyle: React.CSSProperties = {
  height: 44,
  padding: "0 18px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  backgroundColor: "#ffffff",
  zIndex: 30,
  flexShrink: 0,
  borderBottom: "1px solid rgba(226, 232, 240, 0.4)",
};

const statusTimeStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 700,
  color: "#0f172a",
  fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', Roboto, sans-serif",
  letterSpacing: "-0.2px",
  width: 54,
};

const dynamicIslandStyle: React.CSSProperties = {
  width: 98,
  height: 24,
  backgroundColor: "#000000",
  borderRadius: 14,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "0 8px",
  boxSizing: "border-box",
};

const sensorDotStyle: React.CSSProperties = {
  width: 3.5,
  height: 3.5,
  borderRadius: "50%",
  backgroundColor: "#1e293b",
};

const cameraLensStyle: React.CSSProperties = {
  width: 8,
  height: 8,
  borderRadius: "50%",
  backgroundColor: "#0f172a",
  border: "1px solid #1e293b",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const lensReflectionStyle: React.CSSProperties = {
  width: 3,
  height: 3,
  borderRadius: "50%",
  backgroundColor: "#1e3a8a",
  opacity: 0.8,
};

const statusIconsRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  justifyContent: "flex-end",
  width: 54,
};

const phoneContentScrollStyle: React.CSSProperties = {
  flex: 1,
  overflowY: "auto",
};

const homeIndicatorContainerStyle: React.CSSProperties = {
  height: 18,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "#ffffff",
  flexShrink: 0,
};

const homeIndicatorStyle: React.CSSProperties = {
  width: 120,
  height: 4,
  backgroundColor: "#0f172a",
  borderRadius: 2,
  opacity: 0.25,
};

const saveBtnStyle: React.CSSProperties = {
  padding: "10px 24px",
  borderRadius: 8,
  border: "none",
  background: "#1565d8",
  fontSize: 13,
  fontWeight: 700,
  color: "#ffffff",
  cursor: "pointer",
  boxShadow: "0 2px 8px rgba(21, 101, 216, 0.25)",
};

const errorBannerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "10px 14px",
  borderRadius: 8,
  background: "#fef2f2",
  border: "1px solid #fecaca",
  color: "#b91c1c",
  fontSize: 13,
  fontWeight: 600,
};

const successBannerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "10px 14px",
  borderRadius: 8,
  background: "#f0fdf4",
  border: "1px solid #bbf7d0",
  color: "#15803d",
  fontSize: 13,
  fontWeight: 600,
};

const spinnerStyle: React.CSSProperties = {
  width: 26,
  height: 26,
  border: "2px solid #cbd5e1",
  borderTopColor: "#1565d8",
  borderRadius: "50%",
  animation: "spin 0.8s linear infinite",
  margin: "0 auto",
};

export default WhiteLabelling;
